import { DateTimeDisplay, TitleTypeLabel } from '@/exports/columnCommonComponents';
import { FocusAvail } from '@/redux/slices/slice_types';
import FocusAvailEnumMap from './AvailsEnumMap';
import { Box, Typography } from '@mui/material';
import ViewPolarisJobs from '../AvailsSchedule/ViewPolarisJobsButton';
import { capitalizeNoun } from '@/_utils/utils';
import AvailLanguageChipList from '../AvailLanguageChipList';
import AvailLanguageChip from '../AvailLanguageChip';
import FocusImage from '../common/FocusImage';
import styled from '@emotion/styled';
import { EllipsisTooltipTypography } from '@/components/_common/EllipsisTooltipTypography';

export default {
  hid: {
    label: 'Avail ID',
    sortField: 'hid'
  },
  thumbnail: {
    label: '',
    width: 100,
    disableSort: true,
    render: (cell: FocusAvail['version']['title_thumbnail_link'], row: FocusAvail) => (
      <ThumbnailPlaceholder>
        <FocusImage entity={row} />
      </ThumbnailPlaceholder>
    )
  },
  version__title_name: {
    label: 'Title',
    minWidth: 250,
    sortField: 'title_name',
    render: (cell: FocusAvail['version']['title_name'], row: FocusAvail) => (
      <div style={{ maxWidth: '250px' }}>
        <EllipsisTooltipTypography fontWeight="bold">{cell}</EllipsisTooltipTypography>
        {!!row.version.title_name_supplemental?.length && (
          <EllipsisTooltipTypography
            variant="caption"
            sx={{
              display: 'block',
              lineHeight: 1.9,
              fontSize: '.9rem'
            }}
          >
            {row.version.title_name_supplemental}
            {row.version?.season_sequence_order &&
              row.version?.episode_sequence_order &&
              ` S${row.version.season_sequence_order}:E${row.version.episode_sequence_order}`}
          </EllipsisTooltipTypography>
        )}
      </div>
    )
  },
  version__title_type: {
    label: 'Type',
    sortField: 'version__title_type',
    disableSort: true,
    render: (cell: FocusAvail['version']['title_type']) => <TitleTypeLabel type={cell} />
  },
  version__client_ids_supplemental__alpha_id: {
    label: 'Alpha ID',
    minWidth: 120,
    sortField: 'title_client_id',
    render: (cell: FocusAvail['version']['client_ids_supplemental']['alpha_id']) => cell
  },
  version__availed_type: {
    label: 'Version',
    sortField: 'version__availed_type',
    disableSort: true,
    render: (cell: FocusAvail['version']['availed_type']) => <>{capitalizeNoun(cell)}</>
  },
  availed_platform: {
    label: 'Platform',
    sortField: 'availed_platform',
    render: (cell: FocusAvail['availed_platform']) => <FocusAvailEnumMap type="platform" value={cell} />
  },
  availed_av_language: {
    label: 'AV Language',
    disableSort: true,
    render: (cell: FocusAvail['availed_av_language']) => (
      <Typography
        sx={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          maxWidth: '115px'
        }}
      >
        <FocusAvailEnumMap type="language" value={cell} />
      </Typography>
    )
  },
  video_resolution_type: {
    label: 'Format',
    sortField: 'video_resolution_type',
    disableSort: true,
    render: (cell: FocusAvail['video_resolution_type']) => <>{cell.toLocaleUpperCase()}</>
  },
  territories: {
    label: 'Territories',
    disableSort: true,
    render: (cell: FocusAvail['territories']) => (
      <AvailLanguageChip placement="bottom-start" enabled values={cell.map((t) => t.toLocaleUpperCase())} isPointer={false}>
        <div>Territories</div>
      </AvailLanguageChip>
    )
  },
  term_start: {
    sortField: 'term_start',
    label: 'Term Start',
    minWidth: 120,
    render: (cell: FocusAvail['term_start']) => <DateTimeDisplay>{cell}</DateTimeDisplay>
  },
  term_end: {
    sortField: 'term_end',
    label: 'Term End',
    minWidth: 120,
    render: (cell: FocusAvail['term_end']) => <DateTimeDisplay>{cell}</DateTimeDisplay>
  },
  subs: {
    label: 'Subs',
    disableSort: true,
    render: (cell: FocusAvail['allowed_languages'], row: FocusAvail) => (
      <AvailLanguageChipList
        type="sub"
        columns={3}
        countToDisplay={5}
        displayOnly
        languages={[...new Set(row.allowed_languages.filter((lang) => lang.split(':').at(-1) === 'sub').map((lang) => lang.split(':').at(0)!))]}
        elements={row.elements.filter((ele) => ele.element.element_type === 'timedtext')}
      />
    )
  },
  dubs: {
    label: 'Dubs',
    disableSort: true,
    render: (cell: FocusAvail['allowed_languages'], row: FocusAvail) => {
      return (
        <AvailLanguageChipList
          availedAVLanguage={row.availed_av_language}
          type="dub"
          columns={3}
          countToDisplay={5}
          displayOnly
          languages={[...new Set(row.allowed_languages.filter((lang) => lang.split(':').at(-1) === 'dub').map((lang) => lang.split(':').at(0)!))]}
          elements={row.elements.filter((ele) => ele.element.element_type === 'audio' || ele.element.element_type === 'video')}
        />
      );
    }
  },
  license: {
    label: 'Licensing',
    sortField: 'license',
    render: (cell: FocusAvail['license']) => <FocusAvailEnumMap type="license" value={cell} />
  },
  polarisJobs: {
    label: 'Polaris Jobs',
    minWidth: 170,
    disableSort: true,
    render: (cell: FocusAvail, row: FocusAvail) => (
      <ViewPolarisJobs
        schedule={{
          status: 'completed',
          availed_platform: row.availed_platform,
          title: { client_ids_supplemental: { alpha_id: row.version.client_ids_supplemental.alpha_id } }
        }}
        color="tertiary"
        variant="outlined"
      />
    )
  }
};

export const ThumbnailPlaceholder = styled(Box)`
  width: 93px;
  height: 52px;
  border-radius: ${({ theme }) => `${theme.shape.borderRadius}px`};
  background-color: #4a4d5a;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #7a7d8a;
`;
